import { useState, useEffect, useRef } from 'react';
import apiService from '../services/api';

export const useDashboardChanges = (userId) => {
  const [changes, setChanges] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNotification, setShowNotification] = useState(false);
  const intervalRef = useRef(null);
  const hasInitialLoadRef = useRef(false);

  const fetchChanges = async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      
      // Get changes since last visit (only on initial load)
      const changesData = await apiService.get('dashboard/changes');
      setChanges(changesData);
      
      // Show notification if there are changes and it's not first visit
      if (!changesData.is_first_visit && hasSignificantChanges(changesData.changes)) {
        setShowNotification(true);
      }
      
      // Mark dashboard as visited (don't wait for this)
      apiService.post('dashboard/mark-visited').catch(console.warn);
      
    } catch (error) {
      console.error('Failed to fetch dashboard changes:', error);
    } finally {
      setLoading(false);
      hasInitialLoadRef.current = true;
    }
  };

  const updateLastVisited = async () => {
    if (!userId) return;
    
    try {
      // Only update the last visited timestamp, don't fetch changes
      await apiService.post('dashboard/mark-visited');
    } catch (error) {
      console.warn('Failed to update last visited timestamp:', error);
    }
  };

  const hasSignificantChanges = (changes) => {
    if (!changes || typeof changes !== 'object') return false;
    
    return Object.keys(changes).some(key => 
      Array.isArray(changes[key]) && changes[key].length > 0
    );
  };

  const dismissNotification = () => {
    setShowNotification(false);
  };

  // Initial load - fetch changes and show popup if needed
  useEffect(() => {
    fetchChanges();
  }, [userId]);

  // Set up periodic timestamp updates (every minute) - no popup
  useEffect(() => {
    if (!hasInitialLoadRef.current) return;

    intervalRef.current = setInterval(() => {
      updateLastVisited(); // Only update timestamp, don't check for changes
    }, 60000); // Update every minute

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [userId, hasInitialLoadRef.current]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return {
    changes,
    loading,
    showNotification,
    dismissNotification,
    refreshChanges: fetchChanges, // Allow manual refresh if needed
  };
};